import { Project } from '@/components/pages/profile_v2/portfolio/project/interfaces/project'
import { User } from 'containers/user/types/user'
import { ISocialBase, ISocialPlatform, ISocialVerified } from './socials'
import { SubscriptionProductNames } from './subscription'

// ----------------------------------------
// Helper Interfaces
// ----------------------------------------

export interface ConnectionRequestInterface {
  id?: string
  userId: string
  message?: string
  freebird?: boolean
  timestamp?: number
}

export type ConnectionRequestType = (string | ConnectionRequestInterface)[]

export interface IConnectionStat {
  value: string | number
  label: string
}

export interface IConnectionUserInfo {
  username?: string
  image: string
  socials: ISocialPlatform[]
  stats: IConnectionStat[]
}

export interface IUserToken {
  access_token?: string
  id_token?: string
  expires_in?: number
  scope?: 'openid profile email' | 'openid profile phone' // TODO: ask for this
  token_type?: string
}

export interface ProfileData {
  creatorType?: string
  collabInterest?: string
  contentType?: string
  collabOn?: string
  usState?: string
  objectivesOnPlatform?: string
  location?: string
  createdOn?: number
  about?: string
  profileImagePath?: string
}

export interface ResponseUserSave extends IUser {
  user: IUser
  token?: IUserToken
  invite?: { url: string }
  error?: any
}

export enum UserActionTypes {
  SET_LOADING = '[User] Set loading',
  SET_USER = '[User] Set user',
  SET_USER_TOKEN = '[User] Set user token',
  LOG_OUT = '[User] Log out',
  SET_IS_AUTHENTICATED = '[User] Set is authenticated',
}

export interface UserContextProps extends UserState {
  setUser: (user: IUser) => void
  updateUser: (user?: IUser) => void
  logout: (skipRedirect?: boolean, urlRedirect?: string) => void
}

export interface UserSignup {
  firstNam?: string
  lastName?: string
  email?: string
  username?: string
  socials?: ISocialBase[]
  followerCount?: number
  referringUserID?: string
  phone?: string
}

export interface UserState {
  loading?: boolean
  userToken?: IUserToken
  user?: IUser
  isAuthenticated?: boolean
}

export enum UserPreferencesEmails {
  SENDGRID_CONNECTION_REQUESTS = 'email_pref_sendgrid_connection_requests',
  SENDGRID_CONNECTION_REMINDERS = 'email_pref_sendgrid_connection_reminders',
  SENDGRID_CHAT_REMINDERS = 'email_pref_sendgrid_chat_reminders',
  SENDGRID_CONNECTION_UPDATES = 'email_pref_sendgrid_connection_updates',
  MAILCHIMP = 'email_pref_mailchimp',
  BEEHIIV = 'email_pref_beehiiv',
}

// ----------------------------------------
// User Interface
// ----------------------------------------
export interface UserPreferencesAnalytics {
  instagramBasic: { public: boolean }
  instagram: { public: boolean }
  tiktok: { public: boolean }
  youtube: { public: boolean }
}

export interface UserPreferences {
  analytics?: UserPreferencesAnalytics
  affiliateOptIn?: boolean
  emails?: Record<UserPreferencesEmails, boolean>
}

export interface IUser {
  // ----------------------------------------
  // ID
  // ----------------------------------------
  id?: string
  auth0Id?: string
  auth0Ids?: string[]
  stripeIds?: string[]

  // ----------------------------------------
  // Properties (in alphabetical order)
  // ----------------------------------------
  about?: string
  authorized?: boolean
  avatar?: string
  beehiivSubId?: string
  blockedUsers?: string[]
  collabInterest?: string[]
  connections?: string[]
  connectionsReceived?: ConnectionRequestType
  connectionsSent?: ConnectionRequestType
  contentType?: string[]
  converImage?: string
  coverImage?: string
  creatorType?: string[]
  dateOfBirth?: Date
  email?: string
  ethnicity?: string
  externalLink?: {
    linkText: string
    url: string
  }
  firstName?: string
  gender?: string
  invitationsSent?: string[]
  isEmployer?: {
    companyName: string
    companyWebsite: string
  }
  is_complete?: boolean
  isHiring?: boolean
  lastName?: string
  location?: string
  newUser?: boolean
  phone?: string
  preferences?: UserPreferences
  projects?: Project[]
  referredUserIDs?: string[]
  referringUserID?: string
  signupStep?: number
  socials?: ISocialBase[]
  subscriptions?: Record<SubscriptionProductNames, boolean>
  subscribed?: boolean
  title?: string
  totalAudience?: number
  totalVerifiedAudience?: number
  username?: string
  usernameLower?: string
  verifiedSocials?: ISocialVerified

  // ----------------------------------------
  // Legacy Properties
  // ----------------------------------------
  profileData?: ProfileData
}

// ----------------------------------------
// Helpers
// ----------------------------------------

export const getFirstThreeCreatorTypes = (user: IUser | User): string => {
  const copyUser = Object.assign({}, user)
  if (
    typeof copyUser.creatorType !== 'undefined' &&
    Array.isArray(copyUser.creatorType)
  ) {
    if (copyUser.creatorType.length === 0) {
      return ''
    }
    const creatorTypeArray = copyUser.creatorType
    if (copyUser.creatorType.length > 3) {
      const reduceTypes = creatorTypeArray.slice(3)
      return reduceTypes.join(', ')
    }
    return creatorTypeArray.join(', ')
  } else {
    return ''
  }
}

export const getUserShareTitle = (user: IUser | User): string => {
  const formattedName = `${user?.firstName} ${user?.lastName}`
  const formattedCreatorType = user?.creatorType
    ? ` - ${getFirstThreeCreatorTypes(user)}`
    : ''
  const formattedLocation = user.location ? ` - ${user.location}` : ''
  return (
    'Creatorland | ' + formattedName + formattedCreatorType + formattedLocation
  )
}
